// PortalLayout — iOS 26 frosted glass header + bottom tab bar with dynamic indicator
// Top: frosted glass header (Liquid Glass) with real avatar, EN selector, bell, user menu
// Bottom: iOS 26 tab bar with active indicator pill

import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { BottomTabBar } from './UIShell';
import { getUser, clearAuth } from '../../lib/auth';
import { UserAvatar, TierPill } from '../../components/Brand';
import { ToastContainer } from '../../components/Toast';
import { DEMO_CHALLENGE } from '../../data/platforms';

export default function PortalLayout() {
  const nav = useNavigate();
  const user = getUser();
  const displayName = user?.full_name || 'Brandon';
  const userTier = (user as any)?.tier || DEMO_CHALLENGE.tier;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen text-tf-text-1 antialiased" style={{ fontFamily: 'var(--font-sans)', background: 'var(--bg-base)' }}>
      {/* Top header — iOS 26 frosted glass navbar */}
      <header
        className="tf-header sticky top-0 z-40"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
        data-testid="portal-header"
      >
        <div className="relative flex h-12 items-center justify-between px-3">
          <button
            onClick={() => nav('/portal/profile')}
            data-testid="header-avatar-chevron"
            className="flex h-9 items-center gap-1.5 rounded-full pl-0.5 pr-1.5 active:opacity-40 haptic"
          >
            <UserAvatar size={28} name={displayName} />
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2.5">
            <button onClick={() => nav('/portal/search')} aria-label="Search" data-testid="header-search" className="flex h-7 w-7 items-center justify-center rounded-full text-tf-blue active:bg-tf-surface-1 haptic">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
            <button className="flex items-center gap-0.5 text-[14px] font-semibold text-tf-text-1">
              EN
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button
              onClick={() => nav('/portal/notifications')}
              aria-label="Notifications"
              data-testid="header-bell"
              className="relative flex h-7 w-7 items-center justify-center rounded-full text-tf-blue active:bg-tf-surface-1 haptic"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10 21a2 2 0 0 0 4 0" />
              </svg>
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)} aria-label="User menu" data-testid="header-user-menu" className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#007AFF] to-[#5856D6] text-white active:opacity-40 haptic">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div className="w-12" />
        </div>

        {/* User menu dropdown — iOS 26 frosted glass sheet */}
        {menuOpen && (
          <div className="absolute right-3 top-12 z-50 w-64 rounded-[20px] glass-strong p-1.5" data-testid="user-menu" style={{ animation: 'sheet-up 220ms var(--spring-snap)' }}>
            <div className="px-3 py-2.5 flex items-center gap-2.5 border-b border-tf-border mb-1">
              <UserAvatar size={36} name={displayName} />
              <div>
                <div className="text-[14px] font-semibold text-tf-text-1">{displayName}</div>
                <TierPill tier={userTier} />
              </div>
            </div>
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/profile'); }} icon="👤" label="Profile" sub="Name, email, KYC" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/wallet'); }} icon="💳" label="Wallet" sub="Balance, transactions" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/bot'); }} icon="🤖" label="AI Bot Trader" sub="Strategies, settings" />
            {/* Quick shortcuts */}
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">Quick actions</div>
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/deposit-flow'); }} icon="💳" label="Add funds" sub="Top up wallet" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/withdraw-flow'); }} icon="💸" label="Withdraw" sub="Cash out" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/bot'); }} icon="🤖" label="AI bot" sub="Live trading" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/challenge-pricing'); }} icon="🎯" label="Buy challenge" sub="Start a new one" />
            <div className="my-1 border-t border-tf-border" />
            {/* Hub shortcuts — 9 consolidated categories */}
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">Browse all features</div>
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/payments'); }} icon="💰" label="Payments" sub="Wallet, deposits, payouts" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/account'); }} icon="👤" label="Account" sub="Profile, KYC, region" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/security'); }} icon="🔐" label="Security & Privacy" sub="2FA, sessions" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/ai'); }} icon="🤖" label="AI & Personalization" sub="Bot, strategies" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/orders'); }} icon="📊" label="Orders & Activity" sub="Trades, journal" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/risk'); }} icon="🛡️" label="Risk Center" sub="Limits, rules, news" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/challenges'); }} icon="🎯" label="Challenges" sub="Pricing, scaling" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/support'); }} icon="💬" label="Support" sub="FAQ, live chat" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/hub/settings'); }} icon="⚙️" label="Settings" sub="Subscription, API" />
            <div className="my-1 border-t border-tf-border" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/more'); }} icon="📑" label="See all features" sub="Search + 9 hubs" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/profile'); }} icon="👤" label="View profile" sub="Public + private" />
            <MenuItem onClick={() => { setMenuOpen(false); nav('/portal/help-center'); }} icon="💬" label="Help & support" sub="Articles, contact" />
            <div className="my-1 border-t border-tf-border" />
            <MenuItem onClick={() => { clearAuth(); window.location.href = '/'; }} icon="↪️" label="Sign out" color="#FF3B30" />
          </div>
        )}
      </header>

      {/* Dynamic Island-style live indicator (bot running) */}
      <LiveIndicator />

      {/* Page body */}
      <main
        className="min-h-[calc(100vh-3rem)] pb-[calc(56px+env(safe-area-inset-bottom))]"
      >
        <Outlet />
      </main>

      {/* Global Toast container */}
      <ToastContainer />

      {/* Bottom tab bar — iOS 26 frosted glass with active indicator pill */}
      <BottomTabBar
        items={[
          {
            to: '/portal/dashboard',
            label: 'Home',
            testId: 'tab-home',
            icon: <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 12 12 3l9 9" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 10v10h14V10" strokeLinecap="round" strokeLinejoin="round" /></svg>,
          },
          {
            to: '/portal/discover',
            label: 'Discover',
            testId: 'tab-discover',
            icon: <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round" /><path d="M14 7h7v7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
          },
          {
            to: '/portal/orders',
            label: 'Orders',
            testId: 'tab-orders',
            icon: <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" /></svg>,
          },
          {
            to: '/portal/rewards',
            label: 'Rewards',
            testId: 'tab-rewards',
            icon: <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" strokeLinecap="round" /></svg>,
          },
          {
            to: '/portal/more',
            label: 'More',
            testId: 'tab-more',
            icon: <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" fill="currentColor" stroke="none" /></svg>,
          },
        ]}
      />
    </div>
  );
}

function MenuItem({ onClick, icon, label, sub, color }: { onClick: () => void; icon: string; label: string; sub?: string; color?: string }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 text-left active:bg-tf-surface-1 transition haptic"
    >
      <span className="text-[18px]">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold" style={{ color: color || '#1C1C1E' }}>{label}</div>
        {sub && <div className="text-[11px] text-tf-text-3">{sub}</div>}
      </div>
    </button>
  );
}

// iOS 26 Dynamic Island-style live indicator (floating bot status)
// Positioned fixed below the URL bar so it does NOT overlap page content.
function LiveIndicator() {
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 6px)' }}
      data-testid="live-indicator"
    >
      <div
        className="ios26-island pointer-events-auto"
        style={{ padding: '5px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <span className="pulse" />
        <span>AI Bot</span>
        <span style={{ color: 'rgba(255,255,255,0.6)' }}>+184.50</span>
      </div>
    </div>
  );
}
